import { createFileRoute, Link } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';

import { useGetFamilyByStudent } from '~entities/family';
import { useIsStudioV2 } from '~entities/studios/hooks/use-is-studio-v2';

import { FamilyGuardiansPanel } from '~features/contacts/family/family-guardians-panel';
import { Contact } from '~features/contacts/parent/contact';
import { EmergencyContactCards } from '~features/contacts/parent/emergency-contacts';

import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

/**
 * V2: guardians live on the family hub (`/families/:id/contacts`).
 * V1: keep FamilyGuardiansPanel on this student Contacts tab (ENR-3648).
 */
const Component = () => {
  const { studentId } = Route.useParams();
  const { isV2, isLoading: isV2Loading } = useIsStudioV2();
  const { data: familyData, isLoading: isFamilyLoading } =
    useGetFamilyByStudent(isV2 ? studentId : undefined);

  const familyId = familyData?.family?._id;

  return (
    <div className='mb-6'>
      <h2 className='mb-4 text-2xl font-semibold'>Contacts</h2>
      <Contact studentId={studentId} />

      {isV2Loading ? (
        <div className='my-6 flex items-center gap-2 text-sm text-muted-foreground'>
          <Loader2 className='size-4 animate-spin' />
          Loading…
        </div>
      ) : isV2 ? (
        <Card className='my-6 max-w-2xl'>
          <CardHeader>
            <CardTitle className='text-lg'>Family Guardians</CardTitle>
          </CardHeader>
          <CardContent className='space-y-3'>
            <p className='text-sm text-muted-foreground'>
              Guardians, billing, portal access, and reset password are managed
              on the family page for V2 studios.
            </p>
            {isFamilyLoading ? (
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                <Loader2 className='size-4 animate-spin' />
                Looking up family…
              </div>
            ) : familyId ? (
              <Button asChild>
                <Link
                  to='/families/$familyId/contacts'
                  params={{ familyId }}
                >
                  Open family guardians
                </Link>
              </Button>
            ) : (
              <p className='text-sm text-muted-foreground'>
                This student is not on a Family yet. Create one from the family
                tools on the student account, then manage guardians there.
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <h2 className='my-6 text-2xl font-semibold'>Family Guardians</h2>
          <FamilyGuardiansPanel studentId={studentId} />
        </>
      )}

      <h2 className='my-6 text-2xl font-semibold'>Emergency Contacts</h2>
      <EmergencyContactCards studentId={studentId} />
    </div>
  );
};

export const Route = createFileRoute(
  '/_auth/_layout/students/$studentId/contacts',
)({
  component: Component,
});
